// Smart Daara - Shared timetable grid (physical schools)
import React from 'react';
import { useLanguage } from '../../contexts/LanguageContext';

export const DAY_LABELS = {
  fr: ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'],
  en: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
};

export const minToStr = (m) => `${Math.floor(m / 60)}h${String(m % 60).padStart(2, '0')}`;

export const TimetableGrid = ({ entries, config, onCellClick, onEntryClick, labelKey = 'subject_name' }) => {
  const { language } = useLanguage();
  const days = config?.days || [0, 1, 2, 3, 4, 5];
  const dayStart = config?.day_start ?? 480;
  const dayEnd = config?.day_end ?? 1080;
  const slot = config?.slot_minutes || 60;
  const slots = [];
  for (let t = dayStart; t + slot <= dayEnd; t += slot) slots.push(t);
  const breaks = config?.breaks || [];
  const isBreak = (start, end) => breaks.some((b) => start < b.end && b.start < end);
  const entryAt = (day, start) =>
    (entries || []).find((e) => e.day === day && e.start <= start && e.end > start);

  return (
    <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white" data-testid="timetable-grid">
      <table className="w-full text-xs sm:text-sm border-collapse min-w-[640px]">
        <thead>
          <tr className="bg-slate-50">
            <th className="p-2 border-b border-r border-slate-200 text-left text-slate-500 w-20">
              {language === 'fr' ? 'Heure' : 'Time'}
            </th>
            {days.map((d) => (
              <th key={d} className="p-2 border-b border-slate-200 text-slate-700 font-semibold">
                {DAY_LABELS[language === 'fr' ? 'fr' : 'en'][d]}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {slots.map((t) => (
            <tr key={t}>
              <td className="p-2 border-r border-b border-slate-100 text-slate-500 whitespace-nowrap">
                {minToStr(t)}
              </td>
              {days.map((d) => {
                const entry = entryAt(d, t);
                if (entry && entry.start !== t) return <td key={d} className="hidden" />;
                if (entry) {
                  const span = Math.max(1, Math.round((entry.end - entry.start) / slot));
                  return (
                    <td key={d} rowSpan={span} className="p-1 border-b border-slate-100 align-top">
                      <button
                        type="button"
                        onClick={() => onEntryClick && onEntryClick(entry)}
                        className="w-full text-left rounded-md px-2 py-1.5 text-white transition-transform hover:scale-[1.02]"
                        style={{ backgroundColor: entry.subject_color || '#10b981' }}
                        data-testid={`tt-entry-${entry.id}`}
                      >
                        <div className="font-semibold truncate">{entry[labelKey] || entry.subject_name}</div>
                        <div className="opacity-90 truncate">{entry.class_name}</div>
                        <div className="opacity-75 truncate">
                          {entry.teacher_name}{entry.room_name ? ` · ${entry.room_name}` : ''}
                        </div>
                      </button>
                    </td>
                  );
                }
                const brk = isBreak(t, t + slot);
                return (
                  <td
                    key={d}
                    className={`border-b border-slate-100 ${brk ? 'bg-amber-50/60' : onCellClick ? 'cursor-pointer hover:bg-emerald-50' : ''}`}
                    onClick={() => !brk && onCellClick && onCellClick(d, t)}
                    data-testid={`tt-cell-${d}-${t}`}
                  >
                    {brk && <div className="text-[10px] text-center text-amber-500 py-2">{language === 'fr' ? 'Pause' : 'Break'}</div>}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default TimetableGrid;
